import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  makeRequestedAtStr,
  serviceTypeNamesMap,
  timezoneMap,
} from '@open-tender/utils'
import {
  openModal,
  selectCheckout,
  selectOrder,
  selectQuote,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { ButtonLink, Loading } from 'components'
import { getPrepTimeFields } from 'types/prepTime'
import CheckoutSection from './CheckoutSection'
import CheckoutSectionFootnote from './CheckoutSectionFootnote'
import CheckoutSectionTitle from './CheckoutSectionTitle'
import { isFixedQuote } from 'utils/quote'

const CheckoutPickup = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { orderType, requestedAt, revenueCenter, serviceType, prepType } =
    useAppSelector(selectOrder)
  const quote = useAppSelector(selectQuote)
  const { check, loading, submitting } = useAppSelector(selectCheckout)
  // The requestedAt the current check was validated against. After the
  // customer picks a new time, the check on screen is stale until validate
  // returns, so its estimated_at must not be shown next to the new selection.
  // Only settle when a pending validation finishes: requestedAt changes while
  // loading is still idle, and settling then would hide the loader.
  const [settledRequestedAt, setSettledRequestedAt] = useState(requestedAt)
  const prevLoading = useRef(loading)
  useEffect(() => {
    if (prevLoading.current === 'pending' && loading !== 'pending') {
      setSettledRequestedAt(requestedAt)
    }
    prevLoading.current = loading
  }, [loading, requestedAt])
  const isUpdatingTime =
    loading === 'pending' && !submitting && requestedAt !== settledRequestedAt
  const hasFixedQuote = isFixedQuote(quote.order)
  const isMenuEditable = !hasFixedQuote
  if (!revenueCenter || !serviceType) return null
  const { name, address, timezone, first_times } = revenueCenter
  const firstTime = first_times ? first_times[serviceType] : null
  const tz = timezoneMap[timezone]
  const requestedTime = tz ? makeRequestedAtStr(requestedAt, tz, true) : null
  const isAsap = requestedTime === 'ASAP' && serviceType === 'PICKUP'

  // The server already knows the real ready time once the cart has settled
  // (kitchen + dynamic prep), and flags whether it moved. Fall back to the
  // customer's own selection when that isn't available yet -- an older
  // backend, the feature flag off, or before the first validate response.
  // The floor only ever moves a time later, so an estimate earlier than the
  // selection belongs to a previous selection: ignore it until validate returns.
  const checkPrepTime = getPrepTimeFields(check)
  const isStaleCheck =
    Date.parse(checkPrepTime?.estimated_at ?? '') <
    Date.parse(requestedAt ?? '')
  const prepTime = isStaleCheck ? null : checkPrepTime
  const computedTime =
    tz && prepTime ? makeRequestedAtStr(prepTime.estimated_at, tz, true) : null
  // The floor carries seconds, so a slot can be flagged as moved by 20s. Only
  // call it adjusted when the minute the customer sees actually changed.
  const wasAdjusted =
    Boolean(prepTime?.estimated_at_adjusted) && computedTime !== requestedTime
  // ASAP has no customer-picked time to contrast against, so it never shows
  // a strikethrough even when wasAdjusted is true -- but the note below
  // still applies to both variants, per the Figma spec.
  const showOriginal = wasAdjusted && !isAsap

  // ASAP keeps today's "Now (about N mins)" rendering and only switches to
  // the absolute computed time when the server actually pushed the time out
  // -- the case the customer needs to see. Future orders always render the
  // computed time, which equals their own selection unless it moved.
  const useComputed = Boolean(computedTime) && (wasAdjusted || !isAsap)
  const displayTime = useComputed
    ? computedTime
    : isAsap
    ? 'Now'
    : requestedTime
  const waitTime = isAsap && !useComputed ? firstTime?.wait_minutes || null : null
  let serviceTypeName = serviceTypeNamesMap[serviceType]
  serviceTypeName = prepType === 'TAKE_OUT' ? 'Take Out' : serviceTypeName

  const changeLocation = () => {
    navigate('/locations')
  }

  const adjustTime = () => {
    const args = {
      focusFirst: true,
      skipClose: true,
      revenueCenter,
      serviceType,
      orderType,
      requestedAt,
    }
    dispatch(openModal({ type: 'requestedAt', args }))
  }

  return (
    <CheckoutSection title="Location & Time">
      <CheckoutSectionTitle>{name}</CheckoutSectionTitle>
      <p>{address?.street}</p>
      {isUpdatingTime ? (
        <Loading text="Updating time..." style={{ textAlign: 'left' }} />
      ) : (
        <>
          <p>
            {serviceTypeName} Time:{' '}
            {showOriginal ? (
              <>
                <del>{requestedTime}</del> {displayTime}
              </>
            ) : (
              displayTime
            )}
            {waitTime && <span> (about {waitTime} mins)</span>}
          </p>
          {wasAdjusted && (
            <p>
              Updated for your order size so everything is fresh when you
              arrive.
            </p>
          )}
        </>
      )}
      <CheckoutSectionFootnote>
        {isMenuEditable && (
          <p>
            <ButtonLink onClick={changeLocation}>Change location</ButtonLink> or{' '}
            <ButtonLink onClick={adjustTime}>
              adjust {serviceTypeName?.toLowerCase()} time
            </ButtonLink>
          </p>
        )}
      </CheckoutSectionFootnote>
    </CheckoutSection>
  )
}

export default CheckoutPickup
